// src/pages/StoryAppUser.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc, setDoc, updateDoc, increment, serverTimestamp, arrayRemove, arrayUnion, getFirestore, collection, addDoc } from "firebase/firestore";
import {
  getStorage,
  ref as storageRef,
  deleteObject,
} from "firebase/storage";

import {
  Container,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Tooltip,
  CircularProgress,
  Chip,
  Modal,
  Box,
  Switch,
} from "@mui/material";


import VoiceRecorder from "../components/VoiceRecorder";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// ✅ Singletons & helpers from our firebase.js
import { auth, callFunction, ensureUserDoc, recordUserAnalytics } from "../firebase";

/* ---------------- Helpers ---------------- */
const chunkText = (text, maxLength) => {
  const chunks = [];
  for (let i = 0; i < text.length; i += maxLength) chunks.push(text.slice(i, i + maxLength));
  return chunks;
};

const formatStory = (storyText) =>
  String(storyText || "")
    .split("\n")
    .map((line, idx) => (
      <Typography
        key={idx}
        style={{
          fontSize: "1.2rem",
          marginBottom: "10px",
          overflowWrap: "anywhere",
          wordBreak: "break-word",
        }}
      >
        {line}
      </Typography>
    ));


const cleanWordForLookup = (w) =>
  String(w || "")
    .trim()
    .replace(/[^\p{L}\p{N}'-]/gu, "") // keep letters/numbers/'/-
    .replace(/^-+|-+$/g, "")
    .toLowerCase();

const formatStoryClickable = (storyText, onWordClick) =>
  String(storyText || "")
    .split("\n")
    .map((line, idx) => {
      const words = line.split(" ").filter(Boolean);

      return (
        <Typography
          key={idx}
          style={{
            fontSize: "1.2rem",
            marginBottom: "10px",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
          }}
        >
          {words.map((w, i) => (
            <span
              key={`${idx}-${i}-${w}`}
              onClick={() => onWordClick(w)}
              style={{ cursor: "pointer", color: "inherit" }}
              title="Tap for meaning"
            >
              {w}{" "}
            </span>
          ))}
        </Typography>
      );
    });




// 🔵 Inline phonics: highlight words in the main story line
const renderStoryInlinePhonics = (
  storyText,
  phonicsText,
  speakWord,
  setSelectedPhonicsWord
) => {
  const rules = {
    ai: "darkblue", ay: "darkblue", ee: "darkblue", ea: "darkblue",
    ie: "darkblue", oa: "darkblue", oe: "darkblue", ue: "darkblue",
    ow: "darkblue", ou: "darkblue", oi: "darkblue", oy: "darkblue",
    ar: "darkblue", er: "darkblue", ir: "darkblue", or: "darkblue", ur: "darkblue",
    sh: "darkblue", ch: "darkblue", th: "darkblue", wh: "darkblue", ph: "darkblue",
    bl: "darkblue", cl: "darkblue", fl: "darkblue", gl: "darkblue", pl: "darkblue", sl: "darkblue",
    br: "darkblue", cr: "darkblue", dr: "darkblue", fr: "darkblue", gr: "darkblue", pr: "darkblue", tr: "darkblue",
    sc: "darkblue", sk: "darkblue", sm: "darkblue", sn: "darkblue", sp: "darkblue", st: "darkblue", sw: "darkblue",
    qu: "darkblue",
  };

  const clean = (t) =>
    String(t || "")
      .replace(/\[.*?\]/g, "")
      .replace(/<.*?>/g, "")
      .trim();

  const storyLines = clean(storyText)
    .replace(/\.\s+/g, ".\n")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  const phonicsLines = clean(phonicsText)
    .replace(/\.\s+/g, ".\n")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  return storyLines.map((line, idx) => {
    const storyWords = line.split(" ").filter(Boolean);
    const phonicsWords = (phonicsLines[idx] || "").split(" ").filter(Boolean);

    return (
      <Typography
        key={idx}
        style={{
  fontSize: "1.2rem",
  marginBottom: "10px",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
}}

      >
        {storyWords.map((w, i) => {
          const phonicsWord = phonicsWords[i] || "";
          // backend phonics: **sh**ip, ad-ven-ture, etc.
          const syllableForm = phonicsWord.replace(/\*\*/g, ""); // keep dashes
          const lower = syllableForm.toLowerCase();
          const hasMarkers = phonicsWord.includes("**");
          const matchedPattern = Object.keys(rules).find((r) =>
            lower.includes(r)
          );
          const isPhonicsWord = hasMarkers || !!matchedPattern;

        // Find pattern between ** ** if present, e.g. **sh**
let focusPattern = null;
const starMatch = phonicsWord.match(/\*\*(.+?)\*\*/);
if (starMatch && starMatch[1]) {
  focusPattern = starMatch[1]; // e.g. "sh"
} else if (matchedPattern) {
  focusPattern = matchedPattern; // fallback to our local rules
}


          const color = isPhonicsWord
            ? rules[matchedPattern] || "#004d40"
            : "#111";

          const cleanForAudio = syllableForm.replace(/[^a-zA-Z]/g, "");

          return (
            <span
              key={`${w}-${i}`}
              onClick={() => {
                if (cleanForAudio) {
                  speakWord(cleanForAudio);
                }
                if (isPhonicsWord) {
                  setSelectedPhonicsWord({
  display: w.replace(/[^a-zA-Z'-]/g, ""),
  syllables: syllableForm || w,
  pattern: focusPattern || null,
});

                }
              }}
              style={{
                color,
                fontWeight: isPhonicsWord ? "bold" : "normal",
                cursor: "pointer",
                marginRight: 5,
                textDecoration: isPhonicsWord ? "underline" : "none",
              }}
            >
              {w}
            </span>
          );
        })}
      </Typography>
    );
  });
};



/* ---------------- Analytics helper (single source of truth) ---------------- */
const recordAnalytics = async ({ fdb, uid, title, genre, durationMs }) => {
  if (!uid) return;

  const userRef = doc(fdb, "users", uid);
  const now = new Date();
  const today = now.toISOString().split("T")[0];

  // Read current doc to update arrays/maps safely
  let base = {};
  try {
    const snap = await getDoc(userRef);
    base = snap.exists() ? (snap.data() || {}) : {};
  } catch {
    // ignore read failure; we still attempt merges
  }

  // readingActivity: increment storiesGenerated for today
  let activity = Array.isArray(base.readingActivity) ? [...base.readingActivity] : [];
  const idx = activity.findIndex((e) => e.date === today);
  if (idx >= 0) {
    activity[idx].storiesGenerated = Number(activity[idx].storiesGenerated || 0) + 1;
  } else {
    activity.push({ date: today, storiesGenerated: 1, timeSpent: 0 });
  }

  // genresExplored/favoriteGenres
  const fav = { ...(base.favoriteGenres || {}) };
  const explored = { ...(base.genresExplored || {}) };
  if (genre) {
    fav[genre] = Number(fav[genre] || 0) + 1;
    explored[genre] = Number(explored[genre] || 0) + 1;
  }

  // Persist rollups (⚠️ do NOT touch usageMonth/usageUsed here)
  await setDoc(
    userRef,
    {
      readingActivity: activity,
      favoriteGenres: fav,
      genresExplored: explored,
      lastActive: serverTimestamp(),
    },
    { merge: true }
  );

  // Append usage log row (subcollection)
  try {
    await addDoc(collection(fdb, "users", uid, "usageLogs"), {
      type: "story_generate",
      title: title || "Untitled",
      genre: genre || null,
      units: 1,
      durationMs: typeof durationMs === "number" ? durationMs : null,
      createdAt: serverTimestamp(),
    });
  } catch {
    // usage log is best-effort
  }
};

/* ---------------- NEW: save comprehension result to Firestore -------------- */

async function saveComprehensionResult({
  fdb,
  uid,
  storyId = "",
  storyTitle = "",
  genre = "",
  correct = 0,
  total = 0,
  percent = 0,
}) {
  if (!uid) return;

  // Subcollection history (for audits / trends)
  try {
    await addDoc(collection(fdb, "users", uid, "comprehensionResults"), {
      storyId,
      storyTitle,
      genre,
      correct,
      total,
      score: percent,
      percent,
      createdAt: serverTimestamp(),
    });
  } catch (e) {
    console.warn("[comprehension] subcollection write failed:", e?.message || e);
  }

  // Root rollup that PersonalDashboard reads
  try {
    const userRef = doc(fdb, "users", uid);
    await updateDoc(userRef, {
      comprehensionScores: arrayUnion({
        date: new Date().toISOString(),
        score: percent,
        storyTitle,
        genre,
      }),
      lastActive: serverTimestamp(),
    });
  } catch (e) {
    console.warn("[comprehension] rollup write failed:", e?.message || e);
  }
}

/* ---------------- Component ---------------- */
const StoryApp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const storyRef = useRef(null);
  const generationStartRef = useRef(null);
  const wordBankRef = useRef(null);
const questionsRef = useRef(null);
const recorderRef = useRef(null);


  const fdb = getFirestore(); // uses default app instance

  // Parse query
  const queryParams = new URLSearchParams(location.search);
  const selectedProfile = queryParams.get("profile") || "";
  const sourceParam = (queryParams.get("source") || "").toLowerCase();
  const fromParam = queryParams.get("from"); // expected values like "/personal-dashboard"
  const [diversityMode, setDiversityMode] = useState(() => localStorage.getItem("diversityMode") === "true");

  // Auth / ready
  const [authReady, setAuthReady] = useState(false);

  // Optimistic plan/quota (from server)
  const [usage, setUsage] = useState(null);

  // UI + form state
  const [loadingStory, setLoadingStory] = useState(false);
  const [loadingImage, setLoadingImage] = useState(false);
  const [loadingAudio, setLoadingAudio] = useState(false);

  const [story, setStory] = useState("");
  const [storyTitle, setStoryTitle] = useState("");
  const [vocabulary, setVocabulary] = useState([]);
  const [showWordBank, setShowWordBank] = useState(false);
  const [enableWordTap, setEnableWordTap] = useState(false); // ✅ only enable tap-to-define when user wants it

  const [generatedImage, setGeneratedImage] = useState("");
  const [audioSrc, setAudioSrc] = useState("");

  const [comprehensionQuestions, setComprehensionQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [questionsPanelOpen, setQuestionsPanelOpen] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [correctAnswers, setCorrectAnswers] = useState([]);
  const [revealedAnswers, setRevealedAnswers] = useState(false);
  const [score, setScore] = useState(0);
  const [questionMode, setQuestionMode] = useState("multiple_choice"); // or "constructed_response"
  const [constructedMarks, setConstructedMarks] = useState({}); // { [index]: {score,maxScore,feedback,matched,missing} }


  const [phonicsStory, setPhonicsStory] = useState("");
  const [loadingPhonics, setLoadingPhonics] = useState(false);
  const [selectedPhonicsWord, setSelectedPhonicsWord] = useState(null);
  // Tap-any-word definition (6-9+ only)
  const [selectedWord, setSelectedWord] = useState("");
  const [wordDefinition, setWordDefinition] = useState(null);
  const [loadingDefinition, setLoadingDefinition] = useState(false);
  const [definitionError, setDefinitionError] = useState("");
  const [definitionOpen, setDefinitionOpen] = useState(false);

  const [recordings, setRecordings] = useState([]);
  const [showRecorder, setShowRecorder] = useState(false);
  const [showPlayback, setShowPlayback] = useState(false);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState("");
  const [showManageRecordingsModal, setShowManageRecordingsModal] = useState(false);

  const [readingStartTime, setReadingStartTime] = useState(null);
  const [userPlan, setUserPlan] = useState("Free Plan");
  const showThemeLoadedBanner = !!location.state?.theme;


  const genreOptions = ["Fantasy", "Adventure", "Mystery", "Comedy", "Horror", "Suspense", "Action"];
  const themeOptions = ["Friendship", "Courage", "Family", "Magic"];
  const lengthOptions = ["Short", "Medium", "Long"];
  const ageGroupOptions = ["3-5 years old", "6-9 years old", "10-13 years old", "14+ years old"];

  const [formValues, setFormValues] = useState({
    curriculumTopic: "",
    genre: "",
    characters: [],
    setting: "",
    themes: [],
    themeInput: "",
    length: "",
    ageGroup: "",
    storyDescription: "",
    characterDetails: [{ name: "", age: "", description: "" }],
  });

  /* ----------- Auth gate ----------- */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
         setAuthReady(true);
        navigate("/login");
        return;
      }
      
      setAuthReady(true);
      // Ensure user doc exists to avoid Firestore rule errors
      await ensureUserDoc().catch(() => {});
    });
    return () => unsub();
  }, [navigate]);

  /* ----------- Load usage/quota (soft) ----------- */
  useEffect(() => {
    let isMounted = true;
    (async () => {
      if (!authReady) return;
      if (!auth.currentUser) return;
      try {
        let data = null;
  try {
    data = await callFunction("getUsage", {});
  } catch (e) {
    // Fall back silently; the UI will still reflect Firestore mirrors.
    console.warn("getUsage failed, using Firestore mirrors:", e?.message || e);
  }
        if (!isMounted) return;
        setUsage(data || null);
      } catch (e) {
        console.warn("getUsage failed, using fallback:", e?.message || e);
        // Fallback: try to read user doc for local estimate
        try {
          const ref = doc(fdb, "users", auth.currentUser.uid);
          const snap = await getDoc(ref);
          if (snap.exists()) {
            const u = snap.data() || {};
            const ymNow = new Date().toISOString().slice(0, 7);
            const entitlementCap = u.entitlements?.storyQuotaPerMonth;
            const legacyCap = u.plan?.capStories;
            const cap = Number(
              entitlementCap != null ? entitlementCap :
              legacyCap != null ? legacyCap : 3
            );
            const used = u.usageMonth === ymNow ? Number(u.usageUsed || 0) : 0;
            const nowMs = Date.now();
            const addonsRaw = Array.isArray(u.addons) ? u.addons : [];
            const remainingAddons = addonsRaw
              .filter(a => !a?.expiresAt || ((a.expiresAt?._seconds ? a.expiresAt._seconds*1000 : new Date(a.expiresAt).getTime()) > nowMs))
              .reduce((s,a)=>s+Number(a.remaining||0),0);

            setUsage({
              cap,
              used,
              remainingIncluded: Math.max(cap - used, 0),
              remainingAddons,
              remainingTotal: Math.max(cap - used, 0) + remainingAddons,
              usageMonth: u.usageMonth || ymNow,
            });
          }
        } catch {
          // ignore
        }
      }
    })();
    return () => { isMounted = false; };
  }, [authReady, fdb]);

  /* ----------- Fetch plan + recordings (no counters) ----------- */
  useEffect(() => {
    (async () => {
      if (!authReady || !auth.currentUser) return;
      const userRef = doc(fdb, "users", auth.currentUser.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const data = userSnap.data();
        setUserPlan(data?.plan || "Free Plan");
        setRecordings(Array.isArray(data?.recordings) ? data.recordings : []);
      } else {
        setUserPlan("Free Plan");
        setRecordings([]);
      }
    })();
  }, [authReady, fdb]);

  /* ----------- Theme injection from /themes route ----------- */
  useEffect(() => {
    if (location.state?.theme) {
      const { theme, setting, description } = location.state.theme;
      setFormValues((prev) => ({
        ...prev,
        setting: setting || "",
        storyDescription: description || "",
        themes: theme ? [theme] : [],
      }));
      toast.info("✨ Your teacher's theme was loaded.");
    }
  }, [location.state?.theme]);

  /* ----------- Reading timer ----------- */
  useEffect(() => { if (story) setReadingStartTime(Date.now()); }, [story]);

  useEffect(() => {
    const beforeUnload = () => handleFinishReading();
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


    // ⬆️ Scroll to top whenever a new story is generated
  useEffect(() => {
    if (story) {
      window.scrollTo({
        top: 0,
        behavior: "smooth", // or "auto" if you want instant jump
      });
    }
  }, [story]);


const scrollToRef = (ref) => {
  if (!ref?.current) return;
  ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
};


const fetchWordDefinition = async (wordRaw) => {
  const w = cleanWordForLookup(wordRaw);
    // Ignore tiny or non-meaningful words (e.g. "a", "to", "of")
  if (!w || w.length < 3) {
    return;
  }

  

  // Only for 6-9+ (no definitions for youngest)
  if (formValues.ageGroup === "3-5 years old") return;

  setSelectedWord(w);
  setDefinitionError("");
  setLoadingDefinition(true);
  setWordDefinition(null);
  setDefinitionOpen(true);

  try {
    // Free dictionary API (no key)
    const resp = await fetch(`https://api.dictionaryapi.dev/api/v2/entries/en/${encodeURIComponent(w)}`);
    if (!resp.ok) throw new Error("No definition found");

    const data = await resp.json();

    // Extract a clean, simple definition shape
    // data[0].meanings[0].definitions[0].definition
    const entry = Array.isArray(data) ? data[0] : null;
    const meanings = entry?.meanings || [];
    const firstMeaning = meanings[0] || null;
    const partOfSpeech = firstMeaning?.partOfSpeech || "";
    const defs = firstMeaning?.definitions || [];
    const firstDef = defs[0] || null;

    const definition = firstDef?.definition || "";
    const example = firstDef?.example || "";
    const synonyms = Array.isArray(firstDef?.synonyms) ? firstDef.synonyms.slice(0, 6) : [];

    setWordDefinition({
      word: w,
      partOfSpeech,
      definition,
      example,
      synonyms,
    });
  } catch (e) {
    setDefinitionError("Sorry — I couldn’t find a definition for that word.");
  } finally {
    setLoadingDefinition(false);
  }
};



  /* ----------- TTS/Phonics helpers ----------- */
  const speakWord = async (word) => {
    try {
      const processedWord = String(word || "").trim().replace(/[^a-zA-Z]/g, "");
      const resp = await fetch("https://storytelling-backend-i2pl.onrender.com/tts_phonics", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ word: processedWord }),
      });
      if (!resp.ok) throw new Error("TTS request failed");
      const audioBlobLocal = await resp.blob();
      const audioUrl = URL.createObjectURL(audioBlobLocal);
      new Audio(audioUrl).play();
    } catch (e) {
      console.error("Phonics TTS error:", e);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (e) {
      console.error("Logout error:", e);
    }
  };

  const handleFinishReading = async () => {
  try {
    if (!readingStartTime || !auth.currentUser) return;

    const uidLocal = auth.currentUser.uid;
    const userRef = doc(fdb, "users", uidLocal);
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) return;

    const base = userSnap.data() || {};
    const now = new Date();
    const today = now.toISOString().split("T")[0];
    const elapsed = Math.floor((Date.now() - readingStartTime) / 1000);

    // ---------- Root-level rollups (already powering personal dashboards) ----------
    let updatedReadingActivity = Array.isArray(base.readingActivity)
      ? [...base.readingActivity]
      : [];
    const idx = updatedReadingActivity.findIndex((e) => e.date === today);
    if (idx >= 0) {
      updatedReadingActivity[idx].timeSpent =
        Number(updatedReadingActivity[idx].timeSpent || 0) + elapsed;
    } else {
      updatedReadingActivity.push({
        date: today,
        storiesGenerated: 0,
        timeSpent: elapsed,
      });
    }

        const updates = {
      readingActivity: updatedReadingActivity,
      lastReadDate: today,
      lastRead: now.toISOString(),
      totalReadingTime: Number(base.totalReadingTime || 0) + elapsed,
    };


    // ---------- Family profile rollups (feed FamilyDashboard charts) ----------
    if (selectedProfile) {
      const profiles = Array.isArray(base.familyProfiles)
        ? [...base.familyProfiles]
        : [];
      const pIdx = profiles.findIndex((p) => p?.name === selectedProfile);
      if (pIdx >= 0) {
        const pp = { ...profiles[pIdx] };

        // Per-profile readingActivity: append this session
        const pActivity = Array.isArray(pp.readingActivity)
          ? [...pp.readingActivity]
          : [];
        pActivity.push({ date: today, timeSpent: elapsed });
        pp.readingActivity = pActivity;

        // Optional cumulative “buckets” array (kept for backward compat)
        const rTime = Array.isArray(pp.readingTime)
          ? pp.readingTime.slice(0, 4)
          : [0, 0, 0, 0];
        rTime[0] = Number(rTime[0] || 0) + elapsed;
        pp.readingTime = rTime;

        // Simple daily streak logic
        const last = pp.lastReadDate;
        let streak = Number(pp.readingStreak || 0);
        const ymd = (d) => new Date(d).toISOString().slice(0, 10);
        const yest = new Date();
        yest.setDate(yest.getDate() - 1);
        const yesterday = ymd(yest);

        if (last === today) {
          // same day: keep streak as-is
        } else if (last === yesterday) {
          streak = streak + 1;
        } else {
          streak = 1; // new streak
        }
        pp.lastReadDate = today;
        pp.readingStreak = streak;

        profiles[pIdx] = pp;
        updates.familyProfiles = profiles; // include in same write
      }
    }

    await setDoc(userRef, updates, { merge: true });
  } catch (e) {
    console.error("handleFinishReading error:", e);
  }
};


  const handleInputChange = (e) => setFormValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleThemeChange = (e) => {
    const v = e.target.value;
    if (!formValues.themes.includes(v)) {
      setFormValues((prev) => ({ ...prev, themes: [...prev.themes, v] }));
    }
  };

  const handleAddTheme = () => {
    const t = formValues.themeInput.trim();
    if (!t) return;
    setFormValues((prev) => ({ ...prev, themes: [...prev.themes, t], themeInput: "" }));
  };

  const handleRemoveTheme = (i) => {
    const copy = [...formValues.themes];
    copy.splice(i, 1);
    setFormValues((prev) => ({ ...prev, themes: copy }));
  };

  const handleCharacterDetailChange = (i, field, value) => {
    const copy = [...formValues.characterDetails];
    copy[i][field] = value;
    setFormValues((prev) => ({ ...prev, characterDetails: copy }));
  };

  const addCharacter = () => {
    setFormValues((prev) => ({
      ...prev,
      characterDetails: [...prev.characterDetails, { name: "", age: "", description: "" }],
    }));
  };

  const resetForm = () => {
  setFormValues({
    curriculumTopic: "",
    genre: "",
    characters: [],
    setting: "",
    themes: [],
    themeInput: "",
    length: "",
    ageGroup: "",
    storyDescription: "",
    characterDetails: [{ name: "", age: "", description: "" }],
  });

  setStory("");
  setStoryTitle("");
  setAudioSrc("");
  setGeneratedImage("");

  setComprehensionQuestions([]);
  setSelectedAnswers({});
  setCorrectAnswers([]);
  setRevealedAnswers(false);
  setScore(0);

  setVocabulary([]);
  setShowWordBank(false);

  setQuestionMode("multiple_choice");
  setConstructedMarks({});

  setPhonicsStory("");
  setShowRecorder(false);
  setShowPlayback(false);
  setRecordedAudioUrl("");
  setReadingStartTime(null);
  setSelectedPhonicsWord(null);
};


  const generateTTS = async (text) => {
    try {
      if (!text) return;
      setLoadingAudio(true);
      if (text.length > 3000) {
        const parts = chunkText(text, 3000);
        const audioBlobs = [];
        for (const p of parts) {
          const r = await fetch("https://storytelling-backend-i2pl.onrender.com/tts", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ text: p }),
          });
          if (!r.ok) throw new Error("TTS chunk failed");
          audioBlobs.push(await r.blob());
        }
        const merged = new Blob(audioBlobs, { type: "audio/mpeg" });
        setAudioSrc(URL.createObjectURL(merged));
      } else {
        const r = await fetch("https://storytelling-backend-i2pl.onrender.com/tts", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text }),
        });
        if (!r.ok) throw new Error("TTS failed");
        const blob = await r.blob();
        setAudioSrc(URL.createObjectURL(blob));
      }

      // Log playback
      const uidLocal = auth.currentUser?.uid;
      if (uidLocal) {
        const ref = doc(fdb, "users", uidLocal);
        await updateDoc(ref, { ttsPlaybacks: increment(1), lastActive: serverTimestamp() });
      }
    } catch (e) {
      console.error("TTS error:", e);
    } finally {
      setLoadingAudio(false);
    }
  };

  const getQuestionCountForAge = (age) => {
  if (age === "6-9 years old") return 5;
  if (age === "10-13 years old") return 7;
  if (age === "14+ years old") return 9;
  return 3; // fallback (and keeps 3-5 reasonable)
};


  const handleGenerateQuestions = async () => {
    if (!story) return alert("Generate a story first.");
    setLoadingQuestions(true);
    try {
      const resp = await fetch("https://storytelling-backend-i2pl.onrender.com/generate_questions", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
  story,
  ageGroup: formValues.ageGroup || "6-9 years old",
  questionMode,
  numQuestions: getQuestionCountForAge(formValues.ageGroup || "6-9 years old"),
}),

});

if (!resp.ok) throw new Error("Failed to fetch questions");

      const data = await resp.json();
      setComprehensionQuestions(data.comprehensionQuestions || []);
      setCorrectAnswers(data.correctAnswers || []);
      setSelectedAnswers({});
      setConstructedMarks({});
      setRevealedAnswers(false);
      setScore(0);
    } catch (e) {
      console.error("Questions error:", e);
    } finally {
      setLoadingQuestions(false);
    }
  };

  const handleRevealAnswers = () => {
    let correct = 0;
    comprehensionQuestions.forEach((_, i) => {
      const userAns = (selectedAnswers[i] || "").trim().toUpperCase();
      const correctAns = (correctAnswers[i] || "").trim().toUpperCase();
      if (userAns && correctAns && userAns === correctAns) correct++;
    });
    setScore(correct);
    setRevealedAnswers(true);

    // ✅ Persist result
   const total = comprehensionQuestions.length || 0;
   const percent = total ? Math.round((correct / total) * 100) : 0;
   const uidLocal = auth.currentUser?.uid;
   saveComprehensionResult({
     fdb,
     uid: uidLocal,
     storyId: "",                           // fill if you have a story id
     storyTitle: storyTitle || "Untitled",
     genre: formValues.genre || "",
     correct,
     total,
     percent,
   });
  };

  const handleMarkConstructedAnswer = async (index) => {
  try {
    const qObj = comprehensionQuestions[index];
    const answerText = String(selectedAnswers[index] || "").trim();

    if (!answerText) {
      alert("Please write an answer first.");
      return;
    }

    if (!qObj?.answerGuidance) {
      alert("Mark scheme missing for this question.");
      return;
    }

    const resp = await fetch("https://storytelling-backend-i2pl.onrender.com/mark_constructed_answer", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        answer: answerText,
        answerGuidance: qObj.answerGuidance,
      }),
    });

    if (!resp.ok) throw new Error("Marking failed");
    const data = await resp.json();

    setConstructedMarks((prev) => ({ ...prev, [index]: data }));
  } catch (e) {
    console.error("Constructed marking error:", e);
    alert("Could not mark the answer. Please try again.");
  }
};

const getConstructedSummary = () => {
  // Only count questions that actually have a mark
  const marks = comprehensionQuestions
    .map((_, i) => constructedMarks[i])
    .filter(Boolean);

  if (!marks.length) {
    return { totalScore: 0, totalMax: 0, percent: 0, answeredCount: 0, totalQuestions: comprehensionQuestions.length };
  }

  const totalScore = marks.reduce((s, m) => s + Number(m.score || 0), 0);
  const totalMax = marks.reduce((s, m) => s + Number(m.maxScore || 0), 0);
  const percent = totalMax ? Math.round((totalScore / totalMax) * 100) : 0;

  return {
    totalScore,
    totalMax,
    percent,
    answeredCount: marks.length,
    totalQuestions: comprehensionQuestions.length,
  };
};

const handleSaveConstructedScore = async () => {
  const summary = getConstructedSummary();

  if (!comprehensionQuestions.length) {
    alert("No questions to score yet.");
    return;
  }

  // Require all questions to be marked before saving
  if (summary.answeredCount < comprehensionQuestions.length) {
    alert("Please mark all answers before saving your score.");
    return;
  }

  const uidLocal = auth.currentUser?.uid;

  // Save as a % score (keeps dashboards consistent)
  await saveComprehensionResult({
    fdb,
    uid: uidLocal,
    storyId: "",
    storyTitle: storyTitle || "Untitled",
    genre: formValues.genre || "",
    correct: summary.totalScore, // using points
    total: summary.totalMax,     // using max points
    percent: summary.percent,
  });

  toast.success(`✅ Saved written score: ${summary.percent}%`);
};



  const handleGeneratePhonics = async () => {
    if (!story) return alert("Generate a story first.");
    setLoadingPhonics(true);
    setSelectedPhonicsWord(null);
    try {
      const cleaned = String(story).replace(/\[.*?\]/g, "").replace(/<.*?>/g, "").trim();
      const resp = await fetch("https://storytelling-backend-i2pl.onrender.com/generate_phonics", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ story: cleaned, ageGroup: formValues.ageGroup || "default" }),
      });
      if (!resp.ok) throw new Error("Phonics failed");
      const data = await resp.json();
      setPhonicsStory(String(data.phonicsStory || "").trim());
    } catch (e) {
      console.error("Phonics error:", e);
    } finally {
      setLoadingPhonics(false);
    }
  };

  const handleDeleteRecording = async (rec) => {
    try {
      const uidLocal = auth.currentUser?.uid;
      if (!uidLocal) return;
      const storage = getStorage();
      await deleteObject(storageRef(storage, rec.fileName));
      await updateDoc(doc(fdb, "users", uidLocal), { recordings: arrayRemove(rec) });
      setRecordings((r) => r.filter((x) => x.fileName !== rec.fileName));
      if (showManageRecordingsModal && recordings.length - 1 < 3) setShowManageRecordingsModal(false);
      toast.success("✅ Recording deleted.");
    } catch (e) {
      console.error("Delete recording error:", e);
      alert("Failed to delete recording.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!authReady || !auth.currentUser) {
      alert("Please log in again.");
      return;
    }

    // Required fields
    if (!formValues.genre || !formValues.length || !formValues.ageGroup) {
      alert("Please complete the mandatory fields: Genre, Story Length, and Age Group.");
      return;
    }

    setLoadingStory(true);
    setLoadingImage(true);
    setLoadingAudio(false);
    setStory("");
    setStoryTitle("");
    setGeneratedImage("");
    setAudioSrc("");
    setComprehensionQuestions([]);
    setPhonicsStory("");
    setShowRecorder(false);
    setShowPlayback(false);
    setRecordedAudioUrl("");
    setVocabulary([]);
    setShowWordBank(false);
    setQuestionMode("multiple_choice");
    setConstructedMarks({});


    generationStartRef.current = Date.now();

    try {
      // ✅ Atomically consume 1 story (server enforces caps)
      let consume;
      try {
        console.log("consume payload ->", { units: 1, reason: "story_generate" });
        consume = await callFunction("consumeStory", { units: 1, reason: "story_generate" });
      } catch (err) {
        const msg = String(err?.message || "");
        if (/401|403/.test(msg)) {
          alert("The server rejected your token (401). Staying signed in so we can debug.");
          return;
        }
        if (msg.includes("resource-exhausted") || /429/.test(msg)) {
          alert("No stories left this month. You can upgrade or buy an add-on in Subscriptions.");
          navigate("/subscriptions");
          return;
        }
        if (/400/.test(msg)) {
          alert("We couldn’t start generation (Bad Request). Please refresh and try again.");
          return;
        }
        throw err; // unexpected
      }

      if (!consume?.ok) throw new Error("Failed to consume story allowance");

      

      // Locally reflect new remaining counts (snappy UI)
      setUsage((prev) => {
        if (!prev) return prev;
        const used = Number(prev.used ?? 0) + 1;
        const remainingIncluded = Math.max(Number(prev.cap ?? 0) - used, 0);
        let remainingAddons = Number(prev.remainingAddons ?? 0);
        if (remainingIncluded === 0 && remainingAddons > 0 && (prev.remainingTotal ?? 0) > 0) {
          remainingAddons = remainingAddons - 1;
        }
        return {
          ...prev,
          used,
          remainingIncluded,
          remainingAddons,
          remainingTotal: remainingIncluded + remainingAddons,
        };
      });

      // Build prompt payload
      const setting =
        formValues.setting.trim() ||
        (diversityMode ? "A vibrant cultural setting from a real-world region" : "A general adventure setting");

      const characters =
        formValues.characterDetails && formValues.characterDetails.length
          ? formValues.characterDetails
          : diversityMode
          ? [{ name: "A mix of characters from various cultures and backgrounds" }]
          : [{ name: "A mix of regular characters" }];

      const themes =
        formValues.themes && formValues.themes.length
          ? formValues.themes
          : diversityMode
          ? ["Diversity, Inclusion, Cultural Exchange"]
          : ["Friendship, Adventure, Learning"];

      // Optional: profile stat
      try {
        const userRef = doc(fdb, "users", auth.currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists() && selectedProfile) {
          const base = userSnap.data() || {};
          const profiles = (base.familyProfiles || []).map((p) => {
            if (p.name === selectedProfile) {
              const fg = { ...(p.favoriteGenres || {}) };
              if (formValues.genre) fg[formValues.genre] = (fg[formValues.genre] || 0) + 1;
              return {
          ...p,
          // ✅ add this line:
          storiesGenerated: Number(p.storiesGenerated || 0) + 1,
          favoriteGenres: fg,
          recentActivity: [...(p.recentActivity || []), new Date().toISOString()],
        };
            }
            return p;
          });
          await setDoc(userRef, { familyProfiles: profiles }, { merge: true });
        }
      } catch (e2) {
        console.warn("Profile stat update skipped:", e2?.message);
      }

      // Resolve language variant
      const userCountry = await fetch("https://ipapi.co/json/")
        .then((r) => r.json())
        .then((j) => j.country_name)
        .catch(() => "Unknown");
      const languageVariant = userCountry === "United Kingdom" ? "UK English" : "US English";

      // Word count hints
      const hints = {
        "3-5 years old": {
          short: "Make sure the story is exactly 50 words.",
          medium: "Keep the story between 150 and 250 words.",
          long: "Keep the story between 400 and 600 words.",
        },
        "6-9 years old": {
          short: "Make sure the story is around 150 words.",
          medium: "Keep the story between 400 and 600 words.",
          long: "Keep the story between 700 and 850 words.",
        },
        "10-13 years old": {
          short: "Make sure the story is around 400 words.",
          medium: "Keep the story between 700 and 850 words.",
          long: "Keep the story between 950 and 1100 words.",
        },
        "14+ years old": {
          short: "Make sure the story is around 500 words.",
          medium: "Keep the story between 800 and 950 words.",
          long: "Keep the story between 1200 and 1400 words.",
        },
      };
      const lengthKey = String(formValues.length || "").toLowerCase();
      const wordRule = hints[formValues.ageGroup]?.[lengthKey] || "";

      let customPrefix = "";
      if (formValues.ageGroup === "3-5 years old") {
        customPrefix = `
You are writing a story for a child aged 3 to 5 years old.

✅ STRICT RULES:
- Each sentence MUST have between 9 and 12 words.
- DO NOT use fewer than 9 or more than 12 words.
- Each word should be VERY simple and ideally 1–4 letters long.
- Use basic phonics words: cat, dog, run, jump, fun, fish, etc.
- The story MUST NOT exceed 50 words TOTAL.
- Keep it friendly and playful.
- Do NOT use colons, semi-colons, or hyphens to link ideas.
- Do NOT wrap the story in quotation marks or extra formatting.

Use one short sentence per line.

At the start of the story, include a clear title on the first line, like this:
Title: <Your Story Title>

${wordRule}`.trim();
      } else {
        customPrefix = `${wordRule}`;
      }

      const payload = {
        genre: formValues.genre,
        setting,
        themes,
        length: formValues.length,
        ageGroup: formValues.ageGroup,
        storyDescription: `${customPrefix}\n\n${formValues.storyDescription || "An adventure story."}`.trim(),
        characterDetails: characters,
        diversityMode,
        curriculumTopic: formValues.curriculumTopic || "General",
        userCountry,
        languageVariant,
      };

      // Generate story
      const storyResp = await fetch("https://storytelling-backend-i2pl.onrender.com/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!storyResp.ok) throw new Error("Failed to generate story");
      const storyData = await storyResp.json();

      setStory(storyData.story || "");
      setStoryTitle(storyData.title || "Untitled Story");
      setVocabulary(Array.isArray(storyData.vocabulary) ? storyData.vocabulary : []);


        // Fire-and-forget analytics to enrich dashboard rollups
  try {
    const raw = String(storyData.story || "");
    const words = raw.trim() ? raw.trim().split(/\s+/).length : 0;
    await recordUserAnalytics({
      units: 1,
      reason: "story_generate",
      title: storyData.title || "Untitled Story",
      genre: formValues.genre || "",
      durationMs: 0, // we’ll add real reading time in handleFinishReading
      words,
    });
  } catch (e) {
    console.warn("recordUserAnalytics failed (non-fatal):", e?.message || e);
  }


      // ✅ Persist analytics / usage log (only after successful generation)
      try {
        const durationMs = generationStartRef.current ? Date.now() - generationStartRef.current : null;
        await recordAnalytics({
          fdb,
          uid: auth.currentUser.uid,
          title: storyData.title || "Untitled Story",
          genre: formValues.genre || null,
          durationMs,
        });
      } catch (e) {
        console.warn("recordAnalytics failed:", e?.message || e);
      }

      // TTS (non-blocking)
      generateTTS(storyData.story || "");

      // Image
      try {
        const imgResp = await fetch("https://storytelling-backend-i2pl.onrender.com/generate_image", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: setting }),
        });
        if (imgResp.ok) {
          const imgData = await imgResp.json();
          setGeneratedImage(imgData.image_url);
        } else {
          setGeneratedImage("/images/default.png");
        }
      } catch {
        setGeneratedImage("/images/default.png");
      }
    } catch (eAll) {
      console.error("Generate flow error:", eAll);
      const msg = eAll?.message || "Something went wrong while generating your story.";
      alert(msg);
    } finally {
      setLoadingStory(false);
      setLoadingImage(false);
    }
  };

  const backDest = useMemo(() => {
  const allowed = new Set(["/personal-dashboard", "/family-dashboard", "/free-dashboard"]);

  // 1) Explicit 'from' wins
  if (fromParam && allowed.has(fromParam)) return fromParam;

  // 2) Explicit 'source' wins (e.g. ?source=free / ?source=family)
  if (sourceParam === "family") return "/family-dashboard";
  if (sourceParam === "free") return "/free-dashboard";

  // 3) Fallback by plan (only change behaviour for Free users)
  const planLower = String(userPlan || "").toLowerCase();
  if (planLower.includes("free")) return "/free-dashboard";
  if (planLower.includes("family")) return "/family-dashboard";

  // 4) Default: personal
  return "/personal-dashboard";
}, [fromParam, sourceParam, userPlan]);



  const handleNavigation = async (path) => {
    await handleFinishReading();
    navigate(path);
  };

  const generateDisabled = useMemo(() => {
    if (loadingStory) return true;
    if (usage && Number(usage.remainingTotal || 0) <= 0) return true;
    return false;
  }, [usage, loadingStory]);

  if (!authReady) {
    return (
      <div style={{ padding: 24, color: "white" }}>
        <Typography>Loading…</Typography>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundImage: `url(${require("../assets/images/landing_background.jpg")})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "repeat-y",
        minHeight: "100vh",
        fontFamily: "'Baloo Bhaijaan 2', cursive",
        color: "white",
      }}
    >
      {/* Header */}
      <header
        style={{
          width: "100%",
          padding: "10px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.5)",
        }}
      >
        <img
          src={require("../assets/images/logo_name.PNG")}
          alt="YourStoryWorld Logo"
          style={{ height: 40, cursor: "pointer" }}
          onClick={() => navigate("/")}
        />

        <div style={{ display: "flex", gap: 12 }}>
          <Button
            variant="outlined"
            onClick={() => handleNavigation(backDest)}
            style={{ color: "white", borderColor: "white" }}
          >
            Back to Dashboard
          </Button>
          <Button onClick={handleLogout} className="header-button" color="inherit">
            Logout
          </Button>
        </div>
      </header>

      {/* Intro */}
      {!story && (
<Container maxWidth="md" style={{ marginTop: "20px" }}>
  {/* Instructions Section */}
  <Card
    style={{
      marginBottom: "20px",
      padding: "20px",
      backgroundColor: "rgba(0, 0, 0, 0.7)",
      color: "white",
      borderRadius: "10px",
    }}
  >
    <CardContent>
      <Typography
        variant="h4"
        style={{
          textAlign: "center",
          marginBottom: "10px",
          fontWeight: "bold",
        }}
      >
        Welcome to YourStoryWorld!
      </Typography>
      <Typography
        style={{
          textAlign: "justify",
          marginBottom: "10px",
        }}
      >
        To create your personalized story, you can fill out the fields below to shape the adventure to your liking. 
        But don’t worry—if you’d prefer to jump straight into a story, just complete the{" "}
        <span style={{ fontWeight: "bold" }}>minimum mandatory fields</span>, and we’ll take care of the rest!
      </Typography>
      <Typography
        style={{
          textAlign: "justify",
          marginBottom: "10px",
        }}
      >
        Here’s what you can do:
      </Typography>
      <ul style={{ marginLeft: "20px" }}>
        <li>
          <span style={{ fontWeight: "bold" }}>Genre (Mandatory):</span> Choose the type of story you’d like to explore, such as adventure, fantasy, or mystery.
        </li>
        <li>
          <span style={{ fontWeight: "bold" }}>Setting:</span> Decide where your story takes place—a magical forest, a bustling city, or even outer space!
        </li>
        <li>
          <span style={{ fontWeight: "bold" }}>Story Length (Mandatory):</span> Select how long you’d like your story to be (short, medium, or long).
        </li>
        <li>
          <span style={{ fontWeight: "bold" }}>Age Group (Mandatory):</span> Let us know who the story is for so we can adjust the language and content to suit the audience.
        </li>
        <li>
          <span style={{ fontWeight: "bold" }}>Story Description:</span> Provide a brief idea or theme for your story, like "a pirate searching for treasure" or "a scientist discovering a new planet."
        </li>
        <li>
          <span style={{ fontWeight: "bold" }}>Characters:</span> Add details about the characters you want in the story, including names, traits, or roles.
        </li>
      </ul>
      <Typography
        style={{
          textAlign: "justify",
          marginBottom: "10px",
        }}
      >
        Once you've completed your choices, click <span style={{ fontWeight: "bold" }}>Generate Story</span> to watch your personalized adventure come to life! 
        If you'd rather skip the details, just fill in the mandatory fields to enjoy a ready-made story in seconds. You can even listen to your story with our audio playback feature. 🎧
      </Typography>
      <Typography
        style={{
          textAlign: "center",
          fontWeight: "bold",
        }}
      >
        Enjoy your storytelling journey! 🌟
      </Typography>
    </CardContent>
  </Card>
</Container>
)}

      <Container maxWidth="md" style={{ marginTop: "20px" }}>
        {!story ? (
       <>
         {showThemeLoadedBanner && (
  <div
    style={{
      backgroundColor: "#f3ffe5",
      border: "2px solid #76c043",
      color: "#2e7d32",
      padding: "10px 15px",
      borderRadius: "10px",
      marginBottom: "20px",
      textAlign: "center",
      fontWeight: "bold",
      fontSize: "16px",
      boxShadow: "0px 2px 6px rgba(0,0,0,0.1)"
    }}
  >
    ✨ Your teacher's theme was loaded successfully!
  </div>
)}

          <Card
            style={{
              marginBottom: "20px",
              padding: "20px",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              borderRadius: "10px",
              maxWidth: "600px",
              margin: "auto",
            }}
          >

<CardContent>
  {location.state?.theme && (
    <div
      style={{
        backgroundColor: "#e6f4ea",
        border: "2px solid #3cba54",
        padding: "15px",
        borderRadius: "10px",
        marginBottom: "20px",
        boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
      }}
    >
      <Typography variant="h6" style={{ color: "#2e7d32", marginBottom: "8px", fontWeight: "bold" }}>
        ✨ Your teacher assigned a theme!
      </Typography>
      <Typography variant="body1" style={{ fontSize: "15px", color: "#1b5e20" }}>
        <strong>Theme:</strong> {location.state.theme.theme}
      </Typography>
      <Typography variant="body1" style={{ fontSize: "15px", color: "#1b5e20" }}>
        <strong>Setting:</strong> {location.state.theme.setting}
      </Typography>
      <Typography variant="body1" style={{ fontSize: "15px", color: "#1b5e20" }}>
        <strong>Description:</strong> {location.state.theme.description}
      </Typography>
    </div>
  )}



              <Typography variant="h4" style={{ textAlign: "center", marginBottom: 20, color: "#111" }}>
                Create Your Story
              </Typography>

              {/* Mandatory */}
              <Typography variant="h6" style={{ marginBottom: 10, fontWeight: "bold", textDecoration: "underline", color: "#111" }}>
                Mandatory Fields
              </Typography>

              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Tooltip title="Required. Choose your story type." arrow>
                      <div>
                        <TextField
                          select
                          fullWidth
                          name="genre"
                          label="Genre *"
                          value={formValues.genre}
                          onChange={handleInputChange}
                          required
                          InputProps={{ style: { border: "2px solid red" } }}
                        >
                          {genreOptions.map((g) => (
                            <MenuItem key={g} value={g}>{g}</MenuItem>
                          ))}
                        </TextField>
                      </div>
                    </Tooltip>
                  </Grid>

                  <Grid item xs={12}>
                    <Tooltip title="Required. Select how long you’d like the story to be." arrow>
                      <div>
                        <TextField
                          select
                          fullWidth
                          name="length"
                          label="Story Length *"
                          value={formValues.length}
                          onChange={handleInputChange}
                          required
                          InputProps={{ style: { border: "2px solid red" } }}
                        >
                          {lengthOptions.map((o) => (
                            <MenuItem key={o} value={o}>{o}</MenuItem>
                          ))}
                        </TextField>
                      </div>
                    </Tooltip>
                  </Grid>

                  <Grid item xs={12}>
                    <Tooltip title="Required. Tailors language and content to the audience." arrow>
                      <div>
                        <TextField
                          select
                          fullWidth
                          name="ageGroup"
                          label="Age Group *"
                          value={formValues.ageGroup}
                          onChange={handleInputChange}
                          required
                          InputProps={{ style: { border: "2px solid red" } }}
                        >
                          {ageGroupOptions.map((o) => (
                            <MenuItem key={o} value={o}>{o}</MenuItem>
                          ))}
                        </TextField>
                      </div>
                    </Tooltip>
                  </Grid>
                </Grid>

                {/* Optional */}
                <Typography
                  variant="h6"
                  style={{ marginTop: 20, marginBottom: 10, fontWeight: "bold", textDecoration: "underline", color: "#111" }}
                >
                  Optional Fields
                </Typography>

                <Grid item xs={12} style={{ marginBottom: 10 }}>
                  <Typography variant="h6" style={{ marginBottom: 10, color: "#111" }}>Diversity Mode:</Typography>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <input
                      type="checkbox"
                      checked={diversityMode}
                      onChange={() => {
                        const nv = !diversityMode;
                        localStorage.setItem("diversityMode", String(nv));
                        setDiversityMode(nv);
                      }}
                      style={{ transform: "scale(1.3)", cursor: "pointer", marginRight: 10 }}
                    />
                    <span style={{ fontSize: 14, fontWeight: "bold", color: "#111" }}>Enable Diversity Mode</span>
                  </div>
                  <p style={{ fontSize: 12, color: "#555", marginTop: 5 }}>
                    Make characters, settings and themes reflect diverse cultures and backgrounds.
                  </p>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Tooltip title="Optional. Choose where the story takes place, a location, country, a setting etc. " arrow>
                      <div>
                        <TextField
                          fullWidth
                          name="setting"
                          label="Setting"
                          placeholder="E.g., A forest or castle or a bustling marketplace in Morocco"
                          value={formValues.setting}
                          onChange={handleInputChange}
                        />
                      </div>
                    </Tooltip>
                  </Grid>

                  <Grid item xs={12}>
                    <Tooltip title="Optional. Pick a theme to include." arrow>
                      <div>
                        <TextField
                          select
                          fullWidth
                          name="themes"
                          label="Select Theme"
                          value=""
                          onChange={handleThemeChange}
                        >
                          {themeOptions.map((t) => (
                            <MenuItem key={t} value={t}>{t}</MenuItem>
                          ))}
                        </TextField>
                      </div>
                    </Tooltip>
                  </Grid>

                  <Grid item xs={12}>
                    <Tooltip title="Optional. Add your own custom theme, then click Add." arrow>
                      <div>
                        <TextField
                          fullWidth
                          name="themeInput"
                          label="Or Add Your Own Custom Theme"
                          value={formValues.themeInput}
                          onChange={handleInputChange}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleAddTheme();
                            }
                          }}
                        />
                        <Button onClick={handleAddTheme} variant="contained" color="primary" style={{ marginTop: 10 }}>
                          Add Custom Theme
                        </Button>
                      </div>
                    </Tooltip>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h6" style={{ marginBottom: 10, color: "#111" }}>
                      Selected Themes:
                    </Typography>
                    {formValues.themes.map((theme, i) => (
                      <Chip key={i} label={theme} onDelete={() => handleRemoveTheme(i)} style={{ marginRight: 5, marginBottom: 5 }} />
                    ))}
                  </Grid>

                  <Grid item xs={12}>
                    <Tooltip title="Optional. Brief description to steer the story. You can include elements or facts you would like within the story or just state a high level plot for the story....you can even use famous story plots from your favourite books/movies!" arrow>
                      <div>
                       <TextField
  fullWidth
  multiline
  minRows={2}
  maxRows={6}
  name="storyDescription"
  label="Story Description"
  value={formValues.storyDescription}
  onChange={handleInputChange}
/>


                      </div>
                    </Tooltip>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h6" style={{ marginBottom: 10, color: "#111" }}>Character Details:</Typography>
                    {formValues.characterDetails.map((c, i) => (
                      <Grid container spacing={2} key={i} style={{ marginBottom: 10 }}>
                        <Grid item xs={12}>
                          <Tooltip title="Optional. Character name." arrow>
                            <div>
                              <TextField
                                fullWidth
                                label="Name"
                                value={c.name}
                                onChange={(e) => handleCharacterDetailChange(i, "name", e.target.value)}
                                placeholder="E.g., Simon, Joanne or Aisha "
                              />
                            </div>
                          </Tooltip>
                        </Grid>
                        <Grid item xs={6}>
                          <Tooltip title="Optional. Character age." arrow>
                            <div>
                              <TextField
                                fullWidth
                                label="Age"
                                value={c.age}
                                onChange={(e) => handleCharacterDetailChange(i, "age", e.target.value)}
                              />
                            </div>
                          </Tooltip>
                        </Grid>
                        <Grid item xs={12}>
                          <Tooltip title="Optional. You can describe the character physically, personality, their traits, role, or backstory about them." arrow>
                            <div>
                              <TextField
                                fullWidth
                                label="Description"
                                multiline
                                rows={3}
                                value={c.description}
                                onChange={(e) => handleCharacterDetailChange(i, "description", e.target.value)}
                                placeholder="E.g., A brave explorer from Egypt, a cheeky boy with brown curly hair"
                              />
                            </div>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    ))}
                    <Button onClick={addCharacter} variant="contained" color="primary" style={{ margin: "10px 10px 0 0" }}>
                      Add Another Character
                    </Button>
                  </Grid>
                </Grid>

                {/* Generate Story */}
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  fullWidth
                  disabled={generateDisabled}
                  style={{ marginTop: 20 }}
                >
                  {loadingStory ? (
                    <>
                      <CircularProgress size={20} color="inherit" style={{ marginRight: 10 }} />
                      Generating your story…
                    </>
                  ) : (
                    "Generate Story"
                  )}
                </Button>

                {/* If usage loaded and you're out, show a gentle note */}
                {usage && Number(usage.remainingTotal || 0) <= 0 && !loadingStory && (
                  <Typography variant="body2" style={{ color: "#b91c1c", marginTop: 8 }}>
                    You’re out of stories for this period.
                  </Typography>
                )}
              </form>
            </CardContent>
          </Card>
        </>
        ) : (
          <Card
            ref={storyRef}
            style={{
              marginTop: 20,
              padding: 20,
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              borderRadius: 10,
              color: "#111",
            }}
          >
            <CardContent>
              <Typography variant="h4" style={{ fontWeight: "bold", marginBottom: 10 }}>{storyTitle}</Typography>

              {loadingImage ? (
                <CircularProgress style={{ display: "block", margin: "20px auto" }} />
              ) : (
                generatedImage && (
                  <img
                    src={generatedImage}
                    alt="Story Illustration"
                    style={{
                      width: "100%",
                      height: "auto",
                      borderRadius: 10,
                      marginBottom: 20,
                      maxHeight: 400,
                      objectFit: "contain",
                    }}
                  />
                )
              )}

              <Typography variant="subtitle1" style={{ fontStyle: "italic", marginBottom: 20 }}>
                Starring: {formValues.characterDetails.map((c) => c.name).filter(Boolean).join(", ")}
              </Typography>


              {phonicsStory && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 10 }}>
                  <div
                    style={{
                      backgroundColor: "#e0f7fa",
                      padding: "8px 15px",
                      borderRadius: 20,
                      display: "inline-flex",
                      alignItems: "center",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                  >
                    <img
                      src="https://cdn-icons-png.flaticon.com/128/1827/1827427.png"
                      alt="Audio Icon"
                      style={{ width: 18, height: 18, marginRight: 8, opacity: 0.8 }}
                    />
                          <Typography
  variant="body2"
  style={{ color: "#00796b", fontWeight: "bold" }}
>
  Blue, underlined words show phonics sounds.  
  Tap a word to hear it and see how it breaks into syllables.
</Typography>


                  </div>
                </div>
              )}

            {phonicsStory && selectedPhonicsWord && (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      marginBottom: 16,
    }}
  >
    <div
      style={{
        backgroundColor: "#e0f7fa",
        padding: "10px 16px",
        borderRadius: 12,
        maxWidth: 420,
        boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
      }}
    >
      {(() => {
        const { display, syllables, pattern } = selectedPhonicsWord || {};

        let syllableNodes = syllables || "";

        if (pattern) {
          const regex = new RegExp(`(${pattern})`, "i");
          const segments = (syllables || "").split(regex);

          syllableNodes = segments.map((seg, idx) => {
            if (seg.toLowerCase() === pattern.toLowerCase()) {
              return (
                <span
                  key={idx}
                  style={{ color: "#00796b", fontWeight: "bold" }}
                >
                  {seg}
                </span>
              );
            }
            return <span key={idx}>{seg}</span>;
          });
        }

        return (
          <>
            <Typography
              variant="subtitle2"
              style={{ color: "#00796b", textTransform: "uppercase" }}
            >
              Focus word
            </Typography>

            <Typography
              variant="h6"
              style={{ fontWeight: "bold", marginTop: 4, marginBottom: 4 }}
            >
              {display}
            </Typography>

            <Typography
              variant="body1"
              style={{ fontSize: "1rem", marginBottom: 4 }}
            >
              {syllableNodes}
            </Typography>

            {pattern && (
              <Typography variant="caption" style={{ color: "#004d40" }}>
                Focus sound: <strong>{pattern}</strong>
              </Typography>
            )}
          </>
        );
      })()}
    </div>
  </div>
)}

{formValues.ageGroup !== "3-5 years old" && !phonicsStory && enableWordTap && (
  <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
    <div
      style={{
        backgroundColor: "#fff3cd",
        padding: "8px 14px",
        borderRadius: 12,
        maxWidth: 520,
        boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
      }}
    >
      <Typography variant="body2" style={{ color: "#7a5d00", fontWeight: "bold" }}>
        📖 Tip: Tap any word to see its meaning.
      </Typography>
    </div>
  </div>
)}



<div>
  {phonicsStory
    ? renderStoryInlinePhonics(
        String(story),
        String(phonicsStory),
        speakWord,
        setSelectedPhonicsWord
      )
    : formValues.ageGroup !== "3-5 years old" && enableWordTap
      ? formatStoryClickable(story, fetchWordDefinition)
      : formatStory(story)}
</div>


{/* =========================
    Actions (single clean stack)
   ========================= */}
{story && (
  <div
    style={{
      marginBottom: 18,
      padding: 12,
      borderRadius: 12,
      background: "rgba(245,245,245,0.95)",
      border: "1px solid rgba(0,0,0,0.08)",
    }}
  >
    {/* 1) Word meanings (6-9+ only, and only when phonics is NOT shown) */}
    {formValues.ageGroup !== "3-5 years old" && !phonicsStory && (
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
        <Button
          variant={enableWordTap ? "contained" : "outlined"}
          color="primary"
          onClick={() => setEnableWordTap((v) => !v)}
          style={{ minWidth: 210 }}
        >
          {enableWordTap ? "Word dictionary: ON" : "Word dictionary"}
        </Button>

        <Typography variant="body2" style={{ color: "#555" }}>
          Switch to turn on, then tap a word to see its meaning.
        </Typography>
      </div>
    )}

    {/* 2) Word Bank */}
<div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
  <Button
    variant="contained"
    color="primary"
    disabled={!vocabulary || vocabulary.length === 0}
    style={{ minWidth: 210 }}
    onClick={() => {
      const next = !showWordBank;
      setShowWordBank(next);
      if (next) setTimeout(() => scrollToRef(wordBankRef), 80);
    }}
  >
    {showWordBank ? "Hide Word Bank" : "Show Word Bank"}
  </Button>

  <Typography variant="body2" style={{ color: "#555" }}>
    Key vocabulary words from this story to learn (with meanings and examples).
  </Typography>
</div>

{/* 3) Comprehension Questions (not for 3-5) */}
{formValues.ageGroup !== "3-5 years old" && (
  <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
    <Button
      variant="contained"
      color="primary"
      style={{ minWidth: 210 }}
      onClick={() => {
        const next = !questionsPanelOpen;
        setQuestionsPanelOpen(next);
        if (next) setTimeout(() => scrollToRef(questionsRef), 80);
      }}
    >
      {questionsPanelOpen ? "Hide Questions" : "Comprehension Questions"}
    </Button>

    <Typography variant="body2" style={{ color: "#555" }}>
      Test your understanding with quick multiple choice or written questions.
    </Typography>
  </div>
)}

    {/* 4) Start Reading Aloud */}
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
      <Button
  variant="contained"
  color="primary"
  style={{ minWidth: 210 }}
  onClick={() => {
    if (recordings.length >= 3) {
      setShowManageRecordingsModal(true);
      return;
    }

    setShowRecorder(true);

    // ✅ after it renders, scroll to the recorder panel
    setTimeout(() => scrollToRef(recorderRef), 80);
  }}
>
  Start Reading Aloud
</Button>


      <Typography variant="body2" style={{ color: "#555" }}>
        Record yourself reading this story so you can listen to it later! (max 3 recordings).
      </Typography>
    </div>
  </div>
)}





              {["3-5 years old"].includes(formValues.ageGroup) && (
                <Button variant="contained" color="secondary" onClick={handleGeneratePhonics} disabled={loadingPhonics} style={{ marginTop: 8 }}>
                  {loadingPhonics ? "Processing Phonics..." : "Show Phonics"}
                </Button>
              )}

              {/* Audio */}
              {loadingAudio && (
                <div style={{ marginTop: 30 }}>
                  <Typography variant="h6" style={{ fontWeight: "bold", marginBottom: 10 }}>
                    📖 Story Playback
                  </Typography>
                  <Typography style={{ fontStyle: "italic" }}>Processing audio...</Typography>
                </div>
              )}
              {audioSrc && (
                <div style={{ marginTop: 30 }}>
                  <Typography variant="h6" style={{ fontWeight: "bold", marginBottom: 10 }}>
                    📖 Story Playback
                  </Typography>
                  <audio controls style={{ width: "100%" }}>
                    <source src={audioSrc} type="audio/mpeg" />
                    Your browser does not support the audio element.
                  </audio>
                </div>
              )}

              {/* Recorder */}
              {story && (
                <div ref={recorderRef} style={{ marginTop: 30 }}>
                  <Typography variant="h5" style={{ marginBottom: 10, fontWeight: "bold" }}>
                    Your Voice Recording
                  </Typography>

                  

                  {showRecorder && (
                    <div style={{ backgroundColor: "#f5f5f5", padding: 15, borderRadius: 10 }}>
                      <VoiceRecorder
                        recorderLabel="Read-Aloud Recorder"
                        storyTitle={storyTitle}
                        onRecordingComplete={(blobUrl, blob) => {
                          setRecordedAudioUrl(blobUrl);
                          setShowPlayback(true);
                        }}
                      />
                    </div>
                  )}

                  {showPlayback && recordedAudioUrl && (
                    <div style={{ backgroundColor: "#e8f0fe", padding: 15, borderRadius: 10, marginTop: 10 }}>
                      <Typography variant="subtitle1" style={{ marginBottom: 5 }}>
                        Playback:
                      </Typography>
                      <audio controls src={recordedAudioUrl} style={{ width: "100%" }} />
                    </div>
                  )}
                </div>
              )}

             
{/* Word Bank Panel (opened from Actions stack) */}
{showWordBank && vocabulary && vocabulary.length > 0 && (
  <div ref={wordBankRef}>
    <Card
      style={{
        marginTop: 12,
        padding: 16,
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        borderRadius: 10,
        color: "#111",
      }}
    >
      <CardContent>
        <Typography variant="h5" style={{ fontWeight: "bold", marginBottom: 10, color: "#111" }}>
          📚 Word Bank
        </Typography>

        <ul style={{ paddingLeft: 18, margin: 0 }}>
          {vocabulary.map((w, idx) => (
            <li key={idx} style={{ marginBottom: 12 }}>
              <Typography style={{ fontWeight: "bold", color: "#111" }}>{w.word}</Typography>

              {w.definition && (
                <Typography variant="body2" style={{ color: "#111" }}>
                  <strong>Meaning:</strong> {w.definition}
                </Typography>
              )}

              {w.exampleFromStory && (
                <Typography variant="body2" style={{ color: "#111" }}>
                  <strong>In the story:</strong> “{w.exampleFromStory}”
                </Typography>
              )}

              {Array.isArray(w.synonyms) && w.synonyms.length > 0 && (
                <Typography variant="body2" style={{ color: "#111" }}>
                  <strong>Similar words:</strong> {w.synonyms.join(", ")}
                </Typography>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  </div>
)}


{/* Comprehension Questions Panel (opened from Actions stack) */}
{questionsPanelOpen && story && formValues.ageGroup !== "3-5 years old" && (
  <div ref={questionsRef}>
    <Card
      style={{
        marginTop: 12,
        padding: 16,
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        borderRadius: 10,
        color: "#111",
      }}
    >
      <CardContent>
        <Typography variant="h6" style={{ fontWeight: "bold", marginBottom: 10, color: "#111" }}>
          Comprehension Questions
        </Typography>

        {/* Switch with labels either side + explanation */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 10 }}>
          <Typography variant="body2" style={{ color: "#111", fontWeight: "bold" }}>
            Multiple choice
          </Typography>

          <Switch
            checked={questionMode === "constructed_response"}
            onChange={(e) =>
              setQuestionMode(e.target.checked ? "constructed_response" : "multiple_choice")
            }
          />

          <Typography variant="body2" style={{ color: "#111", fontWeight: "bold" }}>
            Written answers
          </Typography>

          <Typography variant="body2" style={{ color: "#555" }}>
            (Multiple choice = quick A/B/C • Written = type an answer and get it auto-marked)
          </Typography>
        </div>

        <Button
          variant="contained"
          color="primary"
          onClick={handleGenerateQuestions}
          disabled={loadingQuestions}
        >
          {loadingQuestions ? "Generating..." : `Generate ${getQuestionCountForAge(formValues.ageGroup)} Questions`}
        </Button>
      </CardContent>
    </Card>
  </div>
)}







              {comprehensionQuestions.length > 0 && (
                <Card style={{ marginTop: 20, padding: 20, backgroundColor: "rgba(255, 255, 255, 0.9)", borderRadius: 10 }}>
                  <CardContent>
                    <Typography variant="h5" style={{ fontWeight: "bold", marginBottom: 10, color: "#111" }}>
                      Comprehension Questions
                    </Typography>
                    <ul>
  {comprehensionQuestions.map((q, i) => {
    const isObject = q && typeof q === "object";

    // -----------------------------
    // Constructed response rendering
    // -----------------------------
    if (questionMode === "constructed_response" && isObject) {
      const mark = constructedMarks[i];

      return (
        <li key={i} style={{ marginBottom: 18 }}>
          <Typography variant="body1" style={{ fontWeight: "bold", color: "#111", marginBottom: 8 }}>
            {q.question || `Question ${i + 1}`}
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Write your answer here..."
            value={selectedAnswers[i] || ""}
            onChange={(e) => setSelectedAnswers((prev) => ({ ...prev, [i]: e.target.value }))}
            disabled={false}
          />

          <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Button variant="contained" color="primary" onClick={() => handleMarkConstructedAnswer(i)}>
              Mark Answer
            </Button>
          </div>

          {mark && (
            <div style={{ marginTop: 10 }}>
              <Typography variant="body2" style={{ fontWeight: "bold", color: "#111" }}>
                Score: {mark.score}/{mark.maxScore}
              </Typography>
              <Typography variant="body2" style={{ color: "#111" }}>
                {mark.feedback}
              </Typography>
              {Array.isArray(mark.missing) && mark.missing.length > 0 && (
                <Typography variant="body2" style={{ color: "#555", marginTop: 6 }}>
                  <strong>Try to include:</strong> {mark.missing.join(", ")}
                </Typography>
              )}
            </div>
          )}
        </li>
      );
    }

    // -----------------------------
    // Multiple choice rendering (legacy)
    // -----------------------------
    const qText = String(q || "");
    return (
      <li key={i} style={{ marginBottom: 15 }}>
        <Typography variant="body1" style={{ fontWeight: "bold", color: "#111" }}>
          {qText.split("\n")[0]}
        </Typography>
        <div>
          {["A", "B", "C"].map((opt) => {
            const line = qText.split("\n").find((ln) => ln.startsWith(`${opt})`)) || `${opt})`;
            return (
              <label key={opt} style={{ display: "block", margin: "5px 0", color: "#111" }}>
                <input
                  type="radio"
                  name={`question-${i}`}
                  value={opt}
                  checked={selectedAnswers[i] === opt}
                  onChange={() => setSelectedAnswers((prev) => ({ ...prev, [i]: opt }))}
                  disabled={revealedAnswers}
                />{" "}
                {line}
              </label>
            );
          })}
        </div>
        {revealedAnswers && (
          <Typography
            variant="body2"
            style={{
              color: selectedAnswers[i] === correctAnswers[i] ? "green" : "red",
              fontWeight: "bold",
            }}
          >
            {selectedAnswers[i] === correctAnswers[i]
              ? "✅ Correct!"
              : `❌ Incorrect! The correct answer was: ${correctAnswers[i]}`}
          </Typography>
        )}
      </li>
    );
  })}
</ul>

{/* Constructed response summary + save */}
{questionMode === "constructed_response" && comprehensionQuestions.length > 0 && (
  <div style={{ marginTop: 10 }}>
    {(() => {
      const s = getConstructedSummary();
      return (
        <>
          <Typography variant="body2" style={{ color: "#111", fontWeight: "bold" }}>
            Marked: {s.answeredCount}/{s.totalQuestions} • Points: {s.totalScore}/{s.totalMax} • Score: {s.percent}%
          </Typography>

          <Button
            variant="contained"
            color="primary"
            style={{ marginTop: 10 }}
            onClick={handleSaveConstructedScore}
            disabled={s.answeredCount < s.totalQuestions}
          >
            Save Written Score
          </Button>

          {s.answeredCount < s.totalQuestions && (
            <Typography variant="body2" style={{ color: "#555", marginTop: 6 }}>
              Mark all answers to enable saving.
            </Typography>
          )}
        </>
      );
    })()}
  </div>
)}


                    {questionMode === "multiple_choice" && !revealedAnswers && (
  <Button variant="contained" color="primary" onClick={handleRevealAnswers} style={{ marginTop: 10 }}>
    Reveal Answers
  </Button>
)}

                    {revealedAnswers && score === correctAnswers.length && (
                      <Typography variant="h6" style={{ color: "green", fontWeight: "bold", marginTop: 10 }}>
                        🎉 Well done! You got all the answers correct!
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Footer buttons */}
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10 }}>
                <Button variant="contained" color="secondary" onClick={() => handleNavigation("/feedback")}>
                  Report Issue
                </Button>

                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    handleFinishReading();
                    resetForm();
                    navigate(backDest, { state: { refresh: true } });
                  }}
                >
                  Return to Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        )}


        {/* Word definition modal (6-9+ only) */}
{definitionOpen && formValues.ageGroup !== "3-5 years old" && (
  <Modal open={definitionOpen} onClose={() => setDefinitionOpen(false)}>
    <Box
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: { xs: "90%", sm: 520 },
        bgcolor: "background.paper",
        borderRadius: 2,
        boxShadow: 24,
        p: 3,
        outline: "none",
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
        Meaning: {selectedWord || ""}
      </Typography>

      {loadingDefinition && (
        <Typography variant="body2" sx={{ mb: 2 }}>
          Looking it up…
        </Typography>
      )}

      {!loadingDefinition && definitionError && (
        <Typography variant="body2" sx={{ color: "#b91c1c", mb: 2 }}>
          {definitionError}
        </Typography>
      )}

      {!loadingDefinition && wordDefinition && (
        <>
          {wordDefinition.partOfSpeech ? (
            <Typography variant="body2" sx={{ color: "#555", mb: 1 }}>
              <strong>Type:</strong> {wordDefinition.partOfSpeech}
            </Typography>
          ) : null}

          {wordDefinition.definition ? (
            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Definition:</strong> {wordDefinition.definition}
            </Typography>
          ) : null}

          {wordDefinition.example ? (
            <Typography variant="body2" sx={{ color: "#555", mb: 2 }}>
              <strong>Example:</strong> “{wordDefinition.example}”
            </Typography>
          ) : null}

          {Array.isArray(wordDefinition.synonyms) && wordDefinition.synonyms.length > 0 ? (
            <Typography variant="body2" sx={{ color: "#555" }}>
              <strong>Similar words:</strong> {wordDefinition.synonyms.join(", ")}
            </Typography>
          ) : null}
        </>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
        <Button variant="contained" onClick={() => setDefinitionOpen(false)}>
          Close
        </Button>
      </div>
    </Box>
  </Modal>
)}


        {/* Manage recordings modal */}
        {showManageRecordingsModal && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
            }}
          >
            <div
              style={{
                position: "relative",
                backgroundColor: "white",
                padding: 20,
                borderRadius: 10,
                width: 400,
                maxHeight: "80vh",
                overflowY: "auto",
                color: "black",
              }}
            >
              <button
                onClick={() => setShowManageRecordingsModal(false)}
                style={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  background: "transparent",
                  border: "none",
                  fontSize: 20,
                  cursor: "pointer",
                }}
                aria-label="Close"
              >
                ❌
              </button>

              <h2 style={{ marginTop: 10 }}>Manage Your Recordings</h2>
              <p>You have reached the maximum of 3 recordings. Please delete one to continue.</p>

              {recordings.map((rec, idx) => (
                <div key={idx} style={{ marginBottom: 10, borderBottom: "1px solid #ccc", paddingBottom: 10 }}>
                  <p><strong>{rec.storyTitle || rec.fileName}</strong></p>
                  <audio controls src={rec.url} style={{ width: "100%" }} />
                  <button
                    style={{
                      marginTop: 8,
                      backgroundColor: "#ff4d4d",
                      color: "white",
                      padding: "6px 12px",
                      border: "none",
                      borderRadius: 5,
                      cursor: "pointer",
                    }}
                    onClick={() => handleDeleteRecording(rec)}
                  >
                    🗑️ Delete
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />

        {/* Disclaimer */}
        <div
          style={{
            textAlign: "center",
            marginTop: 40,
            padding: 20,
            backgroundColor: "#111",
            color: "#ccc",
            borderTop: "1px solid #333",
          }}
        >
          <Typography variant="body2" style={{ fontSize: 13 }}>
            ⚠️ Disclaimer: The stories generated by YourStoryWorld are created using
          AI and are intended for entertainment and educational purposes only.
          While we strive for accuracy, some content may contain factual or
          narrative inconsistencies.
          <p style={{ fontSize: "0.8rem", color: "#e11d48", marginTop: "10px" }}>
  <strong>Child-Safe Content:</strong> YourStoryWorld uses AI safety filters to ensure all stories 
  remain age-appropriate. Violent, explicit, discriminatory, or harmful content is automatically 
  removed or rewritten. Minor variance may occur due to the nature of AI generation.
</p>
          </Typography>
        </div>
      </Container>
    </div>
  );
};

export default StoryApp;
